// pages/ForgotPasswordPage.jsx
import React, { useState, useEffect } from 'react';
import API from '../api';
import { useNavigate, Link } from 'react-router-dom';
import '../styles/global.css';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [touched, setTouched] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [focusedField, setFocusedField] = useState('');
  
  const navigate = useNavigate();

  useEffect(() => {
    setMounted(true);
  }, []);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setTouched(true);

    if (!emailValid) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      await API.post('/users/forgot-password', {
        email: email.trim().toLowerCase()
      });

      setSuccess('OTP sent successfully! Redirecting...');
      
      setTimeout(() => {
        navigate('/reset-password', { 
          state: { email: email.trim().toLowerCase() } 
        });
      }, 1500);

    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to send OTP. Please try again.';
      setError(errorMessage);
      setTimeout(() => setError(''), 5000);
    }

    setLoading(false);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        .page-container {
          min-height: 100vh;
          display: flex;
          font-family: 'Poppins', sans-serif;
          background-color: #1a2b3c;
          background-image: radial-gradient(circle at 10% 20%, rgba(4, 159, 137, 0.1) 0%, transparent 20%),
                            radial-gradient(circle at 90% 80%, rgba(4, 159, 137, 0.1) 0%, transparent 20%);
          overflow: hidden;
          position: relative;
        }

        .mobile-header { display: none; } /* Hidden on Desktop */

        @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-20px); } }
        @keyframes slideUp { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        
        .form-input::placeholder { color: rgba(255,255,255,0.4); }
        
        .form-input:focus {
           border-color: #81e6d9 !important;
           box-shadow: 0 0 0 4px rgba(129, 230, 217, 0.15);
        }

        /* Responsive Styles */
        @media (max-width: 900px) {
          .page-container {
            flex-direction: column;
            overflow-y: auto;
          }
          .left-panel { display: none !important; }
          .right-panel { 
            width: 100% !important; 
            padding: 20px !important;
            display: flex;
            flex-direction: column;
            justify-content: flex-start !important;
            padding-top: 40px !important;
          }
          /* Show Mobile Header */
          .mobile-header {
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            margin-bottom: 30px;
            width: 100%;
          }
          .form-card {
            margin-bottom: 40px;
          }
        }
      `}</style>

      <div className="page-container">
        {/* LEFT PANEL (Desktop Only) */}
        <div className="left-panel" style={styles.leftPanel}>
          <div style={styles.illustrationContainer}>
            <img 
              src="https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Objects/Key.png" 
              alt="Forgot Password 3D" 
              style={styles.heroImage}
            />
            <div style={styles.heroTextContainer}>
              <h1 style={styles.heroTitle}>Lost Your Key?</h1>
              <p style={styles.heroSubtitle}>
                Don't worry, it happens to the best of us. <br/>
                Enter your email and we'll help you get back in.
              </p>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL (Form & Mobile Header) */}
        <div className="right-panel" style={styles.rightPanel}>
          
          {/* MOBILE HEADER (Visible only on Mobile) */}
          <div className="mobile-header">
             <img 
               src="https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Objects/Key.png" 
               alt="Logo" 
               style={{ width: '80px', marginBottom: '15px' }} 
             />
             <div style={{ color: '#81e6d9', fontWeight: 'bold', fontSize: '24px', marginBottom: '5px' }}>
                PharmaLedger
             </div>
             <p style={{ color: '#a0aec0', fontSize: '14px', margin: 0 }}>
                Reset your password securely.
             </p>
          </div>

          <div style={{
            ...styles.formCard,
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(20px)',
          }}>
            <div style={styles.headerSection}>
              <div style={styles.iconCircle}>🔐</div>
              <h2 style={styles.headerTitle}>Forgot Password?</h2>
              <p style={styles.headerSubtitle}>Recover your account access</p>
            </div>

            {success && <div style={styles.successBox}><span>✓</span> {success}</div>}
            {error && <div style={styles.errorBox}><span>⚠</span> {error}</div>}

            <form onSubmit={handleSubmit}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Registered Email</label>
                <div style={styles.inputWrapper(focusedField === 'email', touched && !emailValid)}>
                  <input
                    type="email"
                    className="form-input"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value.trim().toLowerCase());
                      setError('');
                    }}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => { setFocusedField(''); setTouched(true); }}
                    style={styles.input}
                    autoComplete="email"
                  />
                </div>
                {touched && !emailValid && (
                  <div style={styles.fieldError}>• Please enter a valid email</div>
                )}
              </div>

              <button 
                type="submit" 
                disabled={loading || !emailValid} 
                style={styles.submitBtn(loading || !emailValid)}
              >
                {loading ? 'SENDING OTP...' : 'SEND OTP'}
              </button>

              <div style={styles.footerLink}>
                <Link to="/login" style={{ color: '#81e6d9', fontWeight: 600, textDecoration:'none' }}>
                  ← Back to Login
                </Link>
              </div>
              
              <div style={styles.infoBox}>
                💡 Check your spam folder if you don't see the email.
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

// Consistent Styles
const styles = {
  leftPanel: { width: '55%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 1 },
  rightPanel: { width: '25%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 2 },
  illustrationContainer: { width: '100%', maxWidth: '600px', padding: '40px', textAlign: 'center' },
  heroImage: { width: '280px', height: 'auto', animation: 'float 6s ease-in-out infinite', marginBottom: '30px', filter: 'drop-shadow(0 20px 30px rgba(0,0,0,0.5))' },
  heroTextContainer: { animation: 'slideUp 0.8s ease-out' },
  heroTitle: { color: '#fff', fontSize: '36px', fontWeight: '700', marginBottom: '15px', lineHeight: 1.2 },
  heroSubtitle: { color: '#a0aec0', fontSize: '16px', lineHeight: 1.6, maxWidth: '400px', margin: '0 auto' },
  
  formCard: {
    width: '100%', maxWidth: '420px', background: 'rgba(255, 255, 255, 0.05)',
    backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
    borderRadius: '30px', padding: '45px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)', transition: 'all 0.5s ease',
  },
  headerSection: { marginBottom: '30px', textAlign: 'center' },
  iconCircle: { fontSize: '40px', marginBottom: '10px' },
  headerTitle: { color: '#fff', fontSize: '28px', fontWeight: '600', margin: 0 },
  headerSubtitle: { color: '#a0aec0', fontSize: '15px', marginTop: '5px' },
  
  inputGroup: { marginBottom: '20px' },
  label: { color: '#cbd5e0', fontSize: '13px', marginBottom: '8px', display: 'block', fontWeight: '500' },
  inputWrapper: (focused, error) => ({
    background: 'rgba(0, 0, 0, 0.25)', borderRadius: '12px',
    border: error ? '1px solid #e53e3e' : focused ? '1px solid #81e6d9' : '1px solid rgba(255, 255, 255, 0.05)',
    display: 'flex', alignItems: 'center', transition: 'all 0.3s ease',
  }),
  input: { width: '100%', padding: '16px', background: 'transparent', border: 'none', color: '#fff', fontSize: '14px', outline: 'none' },
  
  submitBtn: (disabled) => ({
    width: '100%', padding: '16px', marginTop: '10px',
    background: disabled ? '#4a5568' : '#81e6d9',
    color: disabled ? '#a0aec0' : '#1a202c', border: 'none', borderRadius: '12px',
    fontSize: '14px', fontWeight: '700', cursor: disabled ? 'not-allowed' : 'pointer',
    letterSpacing: '1px', boxShadow: disabled ? 'none' : '0 4px 14px 0 rgba(129, 230, 217, 0.39)',
  }),
  
  footerLink: { textAlign: 'center', marginTop: '25px', fontSize: '13px' },
  errorBox: { background: 'rgba(229, 62, 62, 0.2)', color: '#feb2b2', padding: '12px', borderRadius: '8px', fontSize: '13px', marginBottom: '15px' },
  successBox: { background: 'rgba(72, 187, 120, 0.2)', color: '#9ae6b4', padding: '12px', borderRadius: '8px', fontSize: '13px', marginBottom: '15px' },
  fieldError: { color: '#fc8181', fontSize: '12px', marginTop: '6px' },
  infoBox: { marginTop: '25px', textAlign: 'center', fontSize: '12px', color: '#a0aec0', background: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '8px' }
};